import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, Trash2, Search, ArrowUpCircle, ArrowDownCircle, CheckCircle } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { createContaInput } from '@portal-leo/shared';
import type { z } from 'zod';

type CreateContaInput = z.infer<typeof createContaInput>;

const statusLabels: Record<string, string> = {
  PENDENTE: 'Pendente',
  PAGO: 'Pago',
  VENCIDO: 'Vencido',
  CANCELADO: 'Cancelado',
};

const statusColors: Record<string, string> = {
  PENDENTE: 'bg-amber-100 text-amber-800',
  PAGO: 'bg-green-100 text-green-800',
  VENCIDO: 'bg-red-100 text-red-800',
  CANCELADO: 'bg-gray-100 text-gray-800',
};

const tipoLabels: Record<string, string> = {
  PAGAR: 'A Pagar',
  RECEBER: 'A Receber',
};

const defaultFormValues: CreateContaInput = {
  descricao: '',
  valor: '',
  tipo: 'PAGAR',
  status: 'PENDENTE',
  dataVencimento: '',
  dataPagamento: undefined,
};

export function FinanceiroPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN', 'FINANCEIRO');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConta, setEditingConta] = useState<{ id: string } | null>(null);

  const utils = trpc.useUtils();


  const { data, isLoading } = trpc.contas.list.useQuery({
    page,
    limit: 10,
    search: search || undefined,
    tipo: tipoFilter ? (tipoFilter as 'PAGAR' | 'RECEBER') : undefined,
    status: statusFilter ? (statusFilter as 'PENDENTE' | 'PAGO' | 'VENCIDO' | 'CANCELADO') : undefined,
  });

  const invalidateAll = () => {
    utils.contas.list.invalidate();
    utils.eventos.getFinanceiro.invalidate();
    utils.eventos.listPatrocinadores.invalidate();
    utils.eventos.listGastos.invalidate();
    utils.eventos.getById.invalidate();
    utils.livroCaixa.listLancamentos.invalidate();
    utils.livroCaixa.getResumoFinanceiro.invalidate();
    utils.livroCaixa.getBalancete.invalidate();
    utils.livroCaixa.getSaldosDiarios.invalidate();
  };

  const createMutation = trpc.contas.create.useMutation({
    onSuccess: () => {
      invalidateAll();
      handleCloseModal();
    },
  });

  const updateMutation = trpc.contas.update.useMutation({
    onSuccess: () => {
      invalidateAll();
      handleCloseModal();
    },
  });

  const deleteMutation = trpc.contas.delete.useMutation({
    onSuccess: () => {
      invalidateAll();
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CreateContaInput>({
    resolver: zodResolver(createContaInput),
    defaultValues: defaultFormValues,
  });

  const onSubmit = (data: CreateContaInput) => {
    const payload = {
      descricao: data.descricao,
      valor: data.valor,
      tipo: data.tipo,
      status: data.status,
      dataVencimento: data.dataVencimento,
      dataPagamento: data.dataPagamento && data.dataPagamento !== '' ? data.dataPagamento : undefined,
    };

    if (editingConta) {
      updateMutation.mutate({ id: editingConta.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleEdit = (conta: { id: string; descricao: string; valor: string; tipo: string; status: string; dataVencimento: string; dataPagamento: string | null  }) => {
    setEditingConta(conta);
    setValue('descricao', conta.descricao);
    setValue('valor', conta.valor);
    setValue('tipo', conta.tipo as 'PAGAR' | 'RECEBER');
    setValue('status', conta.status as 'PENDENTE' | 'PAGO' | 'VENCIDO' | 'CANCELADO');
    setValue('dataVencimento', conta.dataVencimento);
    setValue('dataPagamento', conta.dataPagamento || '');
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta conta?')) {
      deleteMutation.mutate({ id });
    }
  };

  const handleMarcarPago = (conta: { id: string }) => {
    updateMutation.mutate({
      id: conta.id,
      status: 'PAGO',
      dataPagamento: new Date().toISOString().split('T')[0],
    });
  };

  const handleOpenModal = () => {
    reset(defaultFormValues);
    setEditingConta(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingConta(null);
    reset(defaultFormValues);
  };

  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Financeiro</h1>
        <Button onClick={handleOpenModal} disabled={!canEdit}>
          <Plus className="mr-2 h-4 w-4" />
          Nova Conta
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <select
              value={tipoFilter}
              onChange={(e) => setTipoFilter(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Todos os tipos</option>
              <option value="PAGAR">A Pagar</option>
              <option value="RECEBER">A Receber</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Todos os status</option>
              <option value="PENDENTE">Pendente</option>
              <option value="PAGO">Pago</option>
              <option value="VENCIDO">Vencido</option>
              <option value="CANCELADO">Cancelado</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.items.map((conta: { id: string; descricao: string; valor: string; tipo: string; status: string; dataVencimento: string; dataPagamento: string | null  }) => (
                    <TableRow key={conta.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {conta.tipo === 'RECEBER' ? (
                            <ArrowUpCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <ArrowDownCircle className="h-5 w-5 text-red-600" />
                          )}
                          <span className="font-medium">{conta.descricao}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                            conta.tipo === 'RECEBER' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {tipoLabels[conta.tipo]}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">{formatCurrency(conta.valor)}</TableCell>
                      <TableCell>{formatDate(conta.dataVencimento)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusColors[conta.status]}`}>
                          {statusLabels[conta.status]}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {conta.status === 'PENDENTE' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarcarPago(conta)}
                              disabled={!canEdit}
                              className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                              title="Marcar como pago"
                            >
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(conta)} disabled={!canEdit} className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(conta.id)} disabled={!canEdit} className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data && <Pagination meta={data.meta} onPageChange={setPage} itemLabel="contas" />}
            </>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingConta ? 'Editar Conta' : 'Nova Conta'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Descrição" {...register('descricao')} error={errors.descricao?.message} />
          <Input
            label="Valor"
            type="number"
            step="0.01"
            {...register('valor')}
            error={errors.valor?.message}
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
              <select
                {...register('tipo')}
                defaultValue="PAGAR"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="PAGAR">A Pagar</option>
                <option value="RECEBER">A Receber</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
              <select
                {...register('status')}
                defaultValue="PENDENTE"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="PENDENTE">Pendente</option>
                <option value="PAGO">Pago</option>
                <option value="VENCIDO">Vencido</option>
                <option value="CANCELADO">Cancelado</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Data Vencimento"
              type="date"
              {...register('dataVencimento')}
              error={errors.dataVencimento?.message}
            />
            <Input
              label="Data Pagamento"
              type="date"
              {...register('dataPagamento')}
              error={errors.dataPagamento?.message}
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
              {editingConta ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default FinanceiroPage;
